import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'community',
  slug: 'community',
  name: 'Community Roadmap',
  description: 'The same public endpoints that power wiki.nexusrmm.ai\'s roadmap board — no API key required, just an allowed origin.',
  icon: 'HowToVote',
  endpoints: [
    {
      slug: 'list-roadmap',
      method: 'GET',
      path: '/api/public/roadmap',
      summary: 'List roadmap items',
      description: 'Public, unauthenticated. Returns every roadmap/feature-request item, sorted by votes. Off by default (404) until an admin enables it and sets an allowed origin under Settings -> Community Wiki.',
      auth: 'public',
      params: [{ name: 'status', in: 'query', type: 'string', description: 'Filter to one status: open, under-review, planned, in-progress, shipped, declined.' }],
      responseExample: `[{ "id": "rm_a1", "title": "Bulk-approve patch rings from mobile", "status": "planned", "votes": 42, "publicResponse": "On the roadmap for next quarter." }]`,
      sourceFile: 'routes/roadmap.js',
    },
    {
      slug: 'submit',
      method: 'POST',
      path: '/api/public/roadmap',
      summary: 'Submit a feature request',
      description: 'Public, unauthenticated, rate-limited. Creates a new item at status "open". This is exactly the request the Feature Requests page on the wiki submits.',
      auth: 'public',
      requestBodyExample: `{ "title": "Bulk-approve patch rings from mobile", "description": "...", "category": "feature" }`,
      responseExample: `{ "ok": true, "id": "rm_a2" }`,
      sourceFile: 'routes/roadmap.js',
    },
    {
      slug: 'vote',
      method: 'POST',
      path: '/api/public/roadmap/:id/vote',
      summary: 'Vote for a roadmap item',
      description: 'Public, unauthenticated. Requires an X-Voter-Token header (any stable per-client string) for dedupe; a repeat vote from the same token+IP returns 409.',
      auth: 'public',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Roadmap item id.' }],
      responseExample: `{ "ok": true, "votes": 43 }`,
      sourceFile: 'routes/roadmap.js',
    },
    {
      slug: 'list-comments',
      method: 'GET',
      path: '/api/public/wiki-comments',
      summary: 'List comments on a page',
      description: 'Public, unauthenticated. Returns every visible comment (and one level of replies) for one wiki or API-docs page, oldest first. Hidden (moderated) comments are omitted.',
      auth: 'public',
      params: [{ name: 'pageSlug', in: 'query', type: 'string', required: true, description: 'The page path comments are attached to, e.g. /rmm/patch-management-you-can-trust.' }],
      responseExample: `[
  { "id": "cm_1a", "pageSlug": "/rmm/patch-management-you-can-trust", "parentId": "", "authorName": "Alex", "body": "Great walkthrough, thanks!", "createdAt": "2026-07-20T10:00:00.000Z" },
  { "id": "cm_1b", "pageSlug": "/rmm/patch-management-you-can-trust", "parentId": "cm_1a", "authorName": "Sam", "body": "Agreed!", "createdAt": "2026-07-20T10:05:00.000Z" }
]`,
      sourceFile: 'routes/wiki-comments.js',
    },
    {
      slug: 'post-comment',
      method: 'POST',
      path: '/api/public/wiki-comments',
      summary: 'Post a comment or reply',
      description: 'Public, unauthenticated, rate-limited. No accounts — authorName is free text. Visible immediately (status "open"); a reply\'s parentId must point at a top-level comment on the same page (one level of nesting only).',
      auth: 'public',
      requestBodyExample: `{ "pageSlug": "/rmm/patch-management-you-can-trust", "authorName": "Alex", "body": "Great walkthrough, thanks!" }`,
      responseExample: `{ "ok": true, "comment": { "id": "cm_1a", "pageSlug": "/rmm/patch-management-you-can-trust", "parentId": "", "authorName": "Alex", "body": "Great walkthrough, thanks!", "createdAt": "2026-07-20T10:00:00.000Z" } }`,
      sourceFile: 'routes/wiki-comments.js',
    },
  ],
};
